import { type SystemStyleObject } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

export const frame = {
  background: 'background/primary',
  borderColor: 'border/regular',
  borderRadius: '4',
  borderWidth: '1',
  boxShadow: 'inset/field',
  transition: 'border 200ms',
  _hover: {
    borderColor: 'border/hover',
  },
  _invalid: {
    borderColor: 'border/error',
    _hover: {
      borderColor: 'border/error',
    },
  },
  _readOnly: {
    background: 'background/disabled',
    _hover: {
      borderColor: 'border/regular',
    },
  },
  _disabled: {
    background: 'background/disabled',
    borderColor: 'border/disabled',
    cursor: 'not-allowed',
    _hover: {
      borderColor: 'border/disabled',
    },
  },
} satisfies SystemStyleObject;

export const text = {
  appearance: 'none',
  color: 'input/text/inputValue',
  textStyle: 'body/lg/regular',
  _placeholder: {
    color: 'input/text/placeholder',
  },
  _disabled: {
    color: 'text/disabled',
    _placeholder: {
      color: 'text/disabled',
    },
  },
} satisfies SystemStyleObject;

export const base = {
  ...frame,
  ...text,
  width: '100%',
  _disabled: {
    ...frame._disabled,
    ...text._disabled,
  },
} satisfies SystemStyleObject;

// Text padding per size, in px. Single source of truth: the `variants` below derive their
// paddingInline/paddingBlock from these, and the textarea recipe reuses them to align the
// status icon with the text (see Textarea.recipe.ts).
export const sizePadding = {
  md: { inline: 11, block: 7 },
  lg: { inline: 15, block: 11 },
} as const;

export const variants = {
  size: {
    md: {
      paddingInline: rem(sizePadding.md.inline),
      paddingBlock: rem(sizePadding.md.block),
      textStyle: 'body/md/regular',
      '--input-height': rem(40),
    },
    lg: {
      paddingInline: rem(sizePadding.lg.inline),
      paddingBlock: rem(sizePadding.lg.block),
      textStyle: 'body/lg/regular',
      '--input-height': rem(48),
    },
  },
} satisfies Record<'size', Record<'md' | 'lg', SystemStyleObject>>;

export const defaultVariants: Record<'size', 'md' | 'lg'> = {
  size: 'lg',
};

/**
 * Additional styles for single-line `<input>` elements to opt out of the textStyle's explicit
 * line-height. With this override the caret follows the font's natural metrics (~font-size ×
 * 1.15) instead of the 20/24px line-height, so it no longer visually extends past sibling icons
 * in input groups. Textarea should NOT use this — multi-line text needs the textStyle line-height.
 */
export const singleLineInputOverrides = {
  lineHeight: 'normal',
} satisfies SystemStyleObject;
